import dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import cloneDeep from 'lodash/cloneDeep';

import type { LogicCondition } from '@webapp/common/entities/logic';
import type { QuestionCol } from '@webapp/common/entities/question-col';
import type { QuestionRow } from '@webapp/common/entities/question-row';
import type { QuestionParams } from '@webapp/common/resources/question';
import { initialQuestionParams } from '@webapp/common/resources/question';

import type { QuestionAnswerActor, QuestionAnswerEntity } from '@webapp/survey/src/entities/answer-machine';
import { QuestionGroupEntity } from '@webapp/survey/src/entities/question-group';
import type { QuestionResponseActor } from '@webapp/survey/src/entities/response-machine';

import type { QuestionGroupModel, QuestionModel, SurveyStore } from '../entities';
import { AnswerType, QuestionType } from '../survey';

// TODO remove, use QuestionContext from machine
export interface QuestionEntity extends QuestionModel {
    answers: Array<QuestionAnswerEntity>;
    groups: Array<QuestionGroupEntity>;
    response: QuestionResponseActor;
    clickReply: boolean;
    timerLeft: Duration;
    residence?: Array<any>;
    selectedAnswerId?: number;
    categories?: Array<IQuestionCategory>;
    conjointConfig?: ConjointConfig;
}

export const withSelfAnswer = (answers: Array<QuestionAnswerActor>): boolean =>
    answers.some((a) => a.getSnapshot().context.type === AnswerType.SELF);

export interface PairComparisonParam {
    answer1_answer: string;
    answer1_filePath: string | null;
    answer1_id: number;
    answer1_order: number;
    answer1_textBlock: string;
    answer1_type: number;

    answer2_answer: string;
    answer2_filePath: string | null;
    answer2_id: number;
    answer2_order: number;
    answer2_textBlock: string;
    answer2_type: number;

    pairNumber: number;
}

export const initialQuestion: Readonly<Question> = {
    fetch: false,
    typeMode: true,
    expand: true,
    previewMode: true,
    pinned: false,
    id: 0,
    name: '',
    pageId: null,
    type: QuestionType.ONE_OF_LIST,
    rows: [],
    cols: [],
    items: {},
    conditions: [],
    params: cloneDeep(initialQuestionParams),
    forwardLogic: [],
    categories: [],
};

export const createQuestion = (
    { groups, ...initial }: Partial<Omit<QuestionEntity, 'groups'>> & { groups: Array<QuestionGroupModel> },
    { info }: SurveyStore,
) => {
    const q = {
        ...initialQuestion,
        ...cloneDeep(initial),
    } as Partial<QuestionEntity>;
    const { clickReply } = info.params.other;

    q.clickReply = clickReply;

    // q.answers = initial.answers.map((a) => new QuestionAnswerEntity(q.id, q.type, a));

    q.groups = (groups ?? []).map((g) => {
        return new QuestionGroupEntity(g /*, this.type*/);
    });

    q.answers = cloneDeep(initial.answers ?? []);

    if (q.params.timer) {
        q.timerLeft = dayjs.duration(
            q.params.hours * 60 * 60 * 1_000 + q.params.minutes * 60 * 1_000 + q.params.seconds * 1_000,
        );
    } else {
        q.timerLeft = dayjs.duration(0);
    }

    switch (q.type) {
        case QuestionType.RESIDENCE:
            // console.error('implement');
            break;
        case QuestionType.FILE_UPLOAD:
            // console.error('implement');
            break;
        case QuestionType.FIGMA_TEST: {
            const figmaTimerSeconds = Number((q.params as any).figmaTimerSeconds) || 0;
            if (figmaTimerSeconds > 0 && !q.params.timer) {
                q.params.timer = true;
                q.params.hours = 0;
                q.params.minutes = 0;
                q.params.seconds = figmaTimerSeconds;
                q.timerLeft = dayjs.duration(figmaTimerSeconds * 1000);
            }
            break;
        }
        default:
            break;
    }

    return q;
};

export type createQuestionRes = ReturnType<typeof createQuestion>;

export class QuestionEntity implements QuestionEntity {}

export interface FillerQuestion {
    name: string;
    type: QuestionType;
    rows: Array<QuestionRow>;
    cols: Array<QuestionCol>;
    items?: AnyObject;
    params: QuestionParams;
    conditions: Array<LogicCondition>;
    categories?: Array<IQuestionCategory>;
}

export type ForwardLogic = {
    id: number;
    questionId: number;
    childQuestionId: number;
    checked: boolean;
    type: string;
    details: Array<{ groupId: number } | { numericValue: number }>;
};

export type ConjointConfigLevel = {
    id: number;
    name: string;
    order: number;
};

export type ConjointConfigAttribute = {
    id: number;
    name: string;
    order: number;
    levels: Array<ConjointConfigLevel>;
};

export type ConjointConfigTaskLevel = {
    id?: number;
    attributeId?: number;
    levelId?: number;
    attributeName?: string;
    levelName?: string;
};

export type ConjointConfigTask = {
    id?: number;
    name: string;
    order?: number;
    cards: Array<{
        id: number;
        order: number;
        name: string;
        levels: Array<ConjointConfigTaskLevel>;
    }>;
};

export type ConjointConfig = {
    attributes: Array<ConjointConfigAttribute>;
    tasks: Array<ConjointConfigTask>;
};

export interface Question extends FillerQuestion {
    // TODO purge unused
    id: number;
    name: string;
    type: QuestionType;
    pageId: number;
    fileId?: number;
    filePath?: string;
    fetch?: boolean;
    expand?: boolean;
    typeMode?: boolean;
    previewMode?: boolean;
    isNew?: boolean;
    pinned?: boolean;
    serial?: number;
    order?: number;
    forwardLogic: Array<ForwardLogic>;
    categories?: Array<IQuestionCategory>;
    maxDiffBlockCount?: number;
    targetRepetitions?: number;
    targetRepetitionsOptions?: Array<{ value: number; isIdeal: boolean }>;
    conjointConfig?: ConjointConfig;
}

export enum QuestionCategoryOrigin {
    USER = 'user',
    RESPONDENT = 'respondent',
}
export interface IQuestionCategory {
    categoryMergersId?: number;
    categoryMergersName?: string;
    createdAt?: string;
    id?: number;
    order?: number;
    origin?: QuestionCategoryOrigin;
    name: string;
    questionId?: number;
    updatedAt?: string;
    parentAnswerId?: number;
    forwardParent?: number;
    hidden?: boolean;
    pinned?: boolean;
    excluded?: boolean;
}

export enum TestAnswerReactingState {
    CORRECT = 'correct',
    INCORRECT = 'incorrect',
    SHOWN = 'shown',
}
